import React, { useState, useRef, useEffect, useCallback } from 'react';

interface DescriptionCellProps {
  text: string | undefined | null;
  expanded: boolean;
  onToggle: (e: React.MouseEvent) => void;
}

/**
 * Renders two <td> elements: the description text + a toggle button column.
 * The button only appears when the text actually overflows (CSS truncation).
 * Uses ResizeObserver to reliably detect overflow after layout/paint.
 */
const DescriptionCells: React.FC<DescriptionCellProps> = ({ text, expanded, onToggle }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  const checkOverflow = useCallback(() => {
    const el = ref.current;
    if (el && !expanded) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [expanded]);

  // Check on mount + whenever text/expanded changes, using rAF to ensure layout is done
  useEffect(() => {
    const id = requestAnimationFrame(checkOverflow);
    return () => cancelAnimationFrame(id);
  }, [checkOverflow, text]);

  // Re-check on resize
  useEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;

    const observer = new ResizeObserver(() => {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, text]);

  return (
    <>
      <td className="px-6 py-4 hidden lg:table-cell max-w-xs">
        {text ? (
          <span ref={ref} className={`text-sm text-neutral-500 block ${expanded ? 'whitespace-pre-wrap' : 'truncate'}`}>
            {text}
          </span>
        ) : (
          <span className="text-xs text-neutral-400">-</span>
        )}
      </td>
      <td className="px-6 py-4 hidden lg:table-cell text-right w-16">
        {(isOverflowing || expanded) && (
          <button
            onClick={onToggle}
            className="px-2 py-0.5 text-[11px] font-medium text-neutral-600 bg-neutral-100 border border-neutral-300 rounded hover:bg-neutral-200 active:bg-neutral-300 transition-colors"
          >
            {expanded ? 'Less' : 'More'}
          </button>
        )}
      </td>
    </>
  );
};

export default DescriptionCells;
